import { useState, useEffect, useRef } from 'react';
import { useOnboarding } from '../OnboardingContext';
import {
  AlertCircle, ChevronRight, Plus, Trash2, Building2, User, Edit2,
  Globe, Shield, X, Search, Check, ChevronDown, ChevronUp, RefreshCw, Loader2,
  UserCheck, Mail, Phone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { isCompanyName } from '@/utils/companyDetection';
import { countriesSorted } from '@/data/countries';
import { FlagImg } from '@/components/ui/CountryFlag';
import { validateTIN, getTINPlaceholder, getTINHint } from '@/utils/tinValidation';
import { PEPDeclarationForm } from './PEPDeclarationForm';
import { supabase } from '@/integrations/supabase/client';
import { phoneCodes, phoneCodesSorted } from '@/data/phoneCodes';

// Portuguese director role → English translation map
const PT_ROLE_MAP: Record<string, string> = {
  'gerente': 'Manager / Director',
  'administrador': 'Director',
  'sócio-gerente': 'Managing Partner',
  'socio-gerente': 'Managing Partner',
  'sócio gerente': 'Managing Partner',
  'socio gerente': 'Managing Partner',
  'presidente': 'President / Chairman',
  'vice-presidente': 'Vice-President',
  'secretário': 'Secretary',
  'secretario': 'Secretary',
  'tesoureiro': 'Treasurer',
  'vogal': 'Board Member',
  'administrador único': 'Sole Director',
  'administrador unico': 'Sole Director',
  'ceo': 'CEO',
  'director': 'Director',
  'diretor': 'Director',
  'representante': 'Representative',
  'procurador': 'Attorney / Proxy',
  'liquidatário': 'Liquidator',
  'liquidatario': 'Liquidator',
  'fiscal único': 'Sole Auditor',
  'fiscal unico': 'Sole Auditor',
  'revisor oficial de contas': 'Statutory Auditor',
  'sócio': 'Partner / Shareholder',
  'socio': 'Partner / Shareholder',
};

function translatePTRole(raw: string): string {
  if (!raw) return raw;
  const key = raw.toLowerCase().trim();
  return PT_ROLE_MAP[key] || raw;
}

const PT_NATIONALITY_MAP: Record<string, string> = {
  'portuguesa': 'Portuguese', 'portugues': 'Portuguese', 'português': 'Portuguese',
  'portuguesa/o': 'Portuguese', 'espanhola': 'Spanish', 'espanhol': 'Spanish',
  'francesa': 'French', 'francês': 'French', 'britânica': 'British',
  'britânico': 'British', 'alemã': 'German', 'alemão': 'German',
  'italiana': 'Italian', 'italiano': 'Italian', 'brasileira': 'Brazilian', 'brasileiro': 'Brazilian',
};

function translatePTNationality(raw: string): string {
  if (!raw) return raw;
  const key = raw.toLowerCase().trim();
  return PT_NATIONALITY_MAP[key] || raw;
}

const COUNTRY_CODE_MAP: Record<string, string> = {
  'PT': 'Portugal', 'GB': 'United Kingdom', 'DE': 'Germany', 'FR': 'France',
  'ES': 'Spain', 'IT': 'Italy', 'NL': 'Netherlands', 'BE': 'Belgium',
  'IE': 'Ireland', 'AT': 'Austria', 'CH': 'Switzerland', 'SE': 'Sweden',
  'NO': 'Norway', 'DK': 'Denmark', 'FI': 'Finland', 'PL': 'Poland',
  'US': 'United States', 'BR': 'Brazil', 'CN': 'China', 'IN': 'India',
};

const COUNTRY_NATIONALITY_MAP: Record<string, string> = {
  'PT': 'Portuguese', 'GB': 'British', 'DE': 'German', 'FR': 'French',
  'ES': 'Spanish', 'IT': 'Italian', 'NL': 'Dutch', 'BE': 'Belgian',
  'IE': 'Irish', 'AT': 'Austrian', 'CH': 'Swiss', 'SE': 'Swedish',
  'NO': 'Norwegian', 'DK': 'Danish', 'FI': 'Finnish', 'PL': 'Polish',
  'US': 'American', 'BR': 'Brazilian', 'CN': 'Chinese', 'IN': 'Indian',
};

function resolveCountry(raw: string | undefined): string {
  if (!raw) return '';
  if (raw.length === 2) return COUNTRY_CODE_MAP[raw.toUpperCase()] || raw;
  return raw;
}

function enrichPerson(person: any, countryCode: string) {
  const isPortuguese = countryCode === 'PT';
  const translatedRole = isPortuguese ? translatePTRole(person.occupation || '') : (person.occupation || '');
  const rawNationality = isPortuguese ? translatePTNationality(person.nationality || '') : (person.nationality || '');
  const nationality = rawNationality || COUNTRY_NATIONALITY_MAP[countryCode?.toUpperCase()] || '';
  const resolvedCountry = resolveCountry(person.address?.country) || resolveCountry(countryCode);
  const street = person.address?.street || '';
  const city = person.address?.city || '';
  const postalCode = person.address?.postalCode || '';
  return {
    ...person,
    occupation: translatedRole,
    nationality,
    address: { street, city, postalCode, country: resolvedCountry },
  };
}

interface TaxResidency {
  code: string;
  name: string;
  flag: string;
  tin?: string;
}

interface TinError {
  code: string;
  error?: string;
}

interface PersonDeclarationData {
  isPEP?: boolean;
  pepDetails?: string;
  taxResidencies?: TaxResidency[];
  isUSResident?: boolean;
  giinNumber?: string;
  declarationsCompleted?: boolean;
}

interface Director extends PersonDeclarationData {
  name: string;
  dateOfBirth: string;
  nationality: string;
  address: { street: string; city: string; postalCode: string; country: string };
  appointedDate: string;
  occupation: string;
  isManuallyAdded?: boolean;
}

interface UBO {
  name: string;
  shareholding: number;
  address: { street: string; city: string; postalCode: string; country: string };
  type: 'individual' | 'company';
  registrationNumber?: string;
  ubos?: UBO[];
}

interface Shareholder extends PersonDeclarationData {
  name: string;
  shareholding: number;
  shareClass: string;
  address: { street: string; city: string; postalCode: string; country: string };
  isManuallyAdded?: boolean;
  type: 'individual' | 'company';
  ubos?: UBO[];
  registrationNumber?: string;
}

// Authorized person contact details (email + phone) for non-applicant persons
interface AuthorizedPersonContact {
  selectedDirectorIndex: number | null; // null = unknown director / someone else
  isApplicant: boolean; // true = the person filling in this form
  isSomeoneElse: boolean; // true = a completely new person not in the director list
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  // Fields only used when isSomeoneElse = true
  fullName?: string;
}

const emptyDirector: Director = {
  name: '', dateOfBirth: '', nationality: '',
  address: { street: '', city: '', postalCode: '', country: '' },
  appointedDate: '', occupation: 'Director', isManuallyAdded: true,
};

const emptyShareholder: Shareholder = {
  name: '', shareholding: 0, shareClass: 'Ordinary',
  address: { street: '', city: '', postalCode: '', country: '' },
  isManuallyAdded: true, type: 'individual', ubos: [],
};

// ─────────────────────────────────────────────────────────────
// Inline declarations sub-component
// ─────────────────────────────────────────────────────────────
interface InlineDeclarationsProps {
  person: PersonDeclarationData & { name: string };
  onChange: (updates: Partial<PersonDeclarationData>) => void;
  fieldErrors?: Record<string, string>;
}

function InlineDeclarations({ person, onChange, fieldErrors = {} }: InlineDeclarationsProps) {
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [tinErrors, setTinErrors] = useState<TinError[]>([]);
  const [showPEPForm, setShowPEPForm] = useState(false);

  const taxResidencies = person.taxResidencies || [];

  const filteredCountries = countriesSorted.filter(
    c => c.name.toLowerCase().includes(countrySearch.toLowerCase()) &&
      !taxResidencies.some(t => t.code === c.code)
  );

  const addCountry = (country: { code: string; name: string; flag: string }) => {
    const updated = [...taxResidencies, { ...country, tin: '' }];
    onChange({ taxResidencies: updated });
    if (country.code === 'US') onChange({ isUSResident: true });
    setShowCountryPicker(false);
    setCountrySearch('');
  };

  const removeCountry = (code: string) => {
    onChange({ taxResidencies: taxResidencies.filter(t => t.code !== code) });
    setTinErrors(prev => prev.filter(e => e.code !== code));
    if (code === 'US') onChange({ isUSResident: false, giinNumber: '' });
  };

  const updateTIN = (code: string, tin: string) => {
    onChange({ taxResidencies: taxResidencies.map(t => t.code === code ? { ...t, tin } : t) });
    if (tin.trim()) {
      const result = validateTIN(code, tin);
      if (!result.isValid) setTinErrors(prev => [...prev.filter(e => e.code !== code), { code, error: result.error }]);
      else setTinErrors(prev => prev.filter(e => e.code !== code));
    } else {
      setTinErrors(prev => prev.filter(e => e.code !== code));
    }
  };

  if (showPEPForm) {
    return (
      <PEPDeclarationForm
        onBack={() => setShowPEPForm(false)}
        onComplete={() => { onChange({ pepDetails: 'Completed via LMAML form' }); setShowPEPForm(false); }}
        personName={person.name}
        personType="Director / Shareholder"
      />
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-border/50 space-y-4">
      {/* PEP Declaration */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-3.5 h-3.5 text-primary" />
          <p className="text-xs font-semibold uppercase tracking-wide">PEP Declaration</p>
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Is this person a Politically Exposed Person or related to one?
        </p>
        <div className={`grid grid-cols-2 gap-2 ${fieldErrors['pep'] ? 'ring-1 ring-destructive rounded-lg p-1' : ''}`}>
          {[false, true].map(val => (
            <button
              key={String(val)}
              onClick={() => { onChange({ isPEP: val }); if (val) setShowPEPForm(true); }}
              className={`py-2 rounded-lg border text-xs font-medium transition-colors ${
                person.isPEP === val
                  ? val ? 'border-amber-500 bg-amber-500/10 text-amber-600' : 'border-green-500 bg-green-500/10 text-green-600'
                  : 'border-border hover:border-primary/50'
              }`}
            >
              {val ? 'Yes — I am a PEP' : 'No — Not a PEP'}
            </button>
          ))}
        </div>
        {fieldErrors['pep'] && <p className="text-xs text-destructive mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors['pep']}</p>}
        {person.isPEP === true && person.pepDetails && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Check className="w-3 h-3 text-success" /> PEP form completed</p>
        )}
      </div>

      {/* Tax Residency */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-3.5 h-3.5 text-primary" />
          <p className="text-xs font-semibold uppercase tracking-wide">Tax Residency & TIN</p>
        </div>
        <div className="space-y-2">
          {taxResidencies.map(country => {
            const tinError = tinErrors.find(e => e.code === country.code);
            const hint = getTINHint(country.code);
            return (
              <div key={country.code} className="p-2 rounded-lg border border-border bg-muted/20">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm flex items-center gap-1.5"><FlagImg code={country.code} size="xs" />{country.name}</span>
                  <button onClick={() => removeCountry(country.code)} className="p-1 hover:bg-muted rounded"><X className="w-3 h-3 text-muted-foreground" /></button>
                </div>
                {hint && <p className="text-xs text-muted-foreground mb-1">Format: {hint}</p>}
                <Input value={country.tin || ''} onChange={e => updateTIN(country.code, e.target.value)}
                  placeholder={getTINPlaceholder(country.code)}
                  className={`h-8 text-xs ${tinError ? 'border-destructive' : ''}`} />
                {tinError && <p className="text-xs text-destructive mt-0.5">{tinError.error}</p>}
              </div>
            );
          })}
        </div>
        {fieldErrors['taxResidency'] && <p className="text-xs text-destructive mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors['taxResidency']}</p>}
        {showCountryPicker ? (
          <div className="mt-2 space-y-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input type="text" value={countrySearch} onChange={e => setCountrySearch(e.target.value)}
                placeholder="Search country..." autoFocus
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-muted/30 border border-border focus:outline-none" />
            </div>
            <div className="max-h-28 overflow-y-auto border border-border rounded-lg">
              {filteredCountries.slice(0, 8).map(c => (
                <button key={c.code} onClick={() => addCountry(c)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 text-left text-xs border-b border-border last:border-b-0">
                  <FlagImg code={c.code} size="xs" />{c.name}
                </button>
              ))}
              {filteredCountries.length === 0 && <p className="text-center py-3 text-xs text-muted-foreground">No countries found</p>}
            </div>
            <button onClick={() => { setShowCountryPicker(false); setCountrySearch(''); }}
              className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setShowCountryPicker(true)}
            className="mt-2 w-full h-8 rounded-lg border-2 border-dashed border-muted-foreground/30 text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1 text-xs">
            <Plus className="w-3 h-3" /> Add Tax Country
          </button>
        )}
      </div>

      {/* FATCA */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-3.5 h-3.5 text-primary" />
          <p className="text-xs font-semibold uppercase tracking-wide">US Person Declaration (FATCA)</p>
        </div>
        <p className="text-xs text-muted-foreground mb-2">Is this person a US citizen, US resident, or otherwise subject to US tax reporting?</p>
        <div className={`grid grid-cols-2 gap-2 ${fieldErrors['fatca'] ? 'ring-1 ring-destructive rounded-lg p-1' : ''}`}>
          {[false, true].map(val => (
            <button key={String(val)} onClick={() => onChange({ isUSResident: val })}
              className={`py-2 rounded-lg border text-xs font-medium transition-colors ${
                person.isUSResident === val
                  ? val ? 'border-amber-500 bg-amber-500/10 text-amber-600' : 'border-green-500 bg-green-500/10 text-green-600'
                  : 'border-border hover:border-primary/50'
              }`}>
              {val ? 'Yes — US Person' : 'No — Not a US Person'}
            </button>
          ))}
        </div>
        {fieldErrors['fatca'] && <p className="text-xs text-destructive mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{fieldErrors['fatca']}</p>}
        {person.isUSResident === true && (
          <div className="mt-2">
            <p className="text-xs text-muted-foreground mb-1">GIIN Number (if applicable):</p>
            <Input value={person.giinNumber || ''} onChange={e => onChange({ giinNumber: e.target.value })}
              placeholder="e.g. XXXXXX.XXXXX.SL.XXX" className="h-8 text-xs" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Validation helpers
// ─────────────────────────────────────────────────────────────
function getDirectorErrors(d: Director): string[] {
  const errors: string[] = [];
  if (!d.name?.trim()) errors.push('Full name is required');
  // PEP / Tax residency / FATCA declarations are now collected AFTER Sumsub identity
  // verification in the dedicated `corporate-individual-declarations` step.
  return errors;
}

function getDirectorFieldErrors(d: Director): Record<string, string> {
  const e: Record<string, string> = {};
  if (!d.name?.trim()) e['name'] = 'Required';
  return e;
}

function getShareholderErrors(s: Shareholder, _directorNames: string[] = []): string[] {
  const errors: string[] = [];
  if (!s.name?.trim()) errors.push('Name is required');
  if (!s.shareholding || s.shareholding <= 0) errors.push('Shareholding percentage is required');
  // PEP / Tax / FATCA collected later — see corporate-individual-declarations step.
  return errors;
}

function getShareholderFieldErrors(s: Shareholder, _directorNames: string[] = []): Record<string, string> {
  const e: Record<string, string> = {};
  if (!s.name?.trim()) e['name'] = 'Required';
  if (!s.shareholding || s.shareholding <= 0) e['shareholding'] = 'Required';
  return e;
}

// ─────────────────────────────────────────────────────────────
// Main Step component
// ─────────────────────────────────────────────────────────────
export function CorporateDirectorsShareholdersStep() {
  const { data, updateData, goToNextStep } = useOnboarding();
  const company = data.corporateCompany;

  const [directors, setDirectors] = useState<Director[]>(() =>
    (company?.directors || []).map(d => enrichPerson(d, company?.countryCode || ''))
  );
  const [shareholders, setShareholders] = useState<Shareholder[]>(() => {
    const existing = company?.shareholders || [];
    const dirList = (company?.directors || []).map((d: any) => enrichPerson(d, company?.countryCode || ''));
    return existing.map(s => {
      const enriched = {
        ...enrichPerson(s, company?.countryCode || ''),
        type: (s.type || (isCompanyName(s.name) ? 'company' : 'individual')) as 'individual' | 'company',
        ubos: s.ubos || [],
      };
      const matchedDir = dirList.find((d: any) => d.name?.toLowerCase().trim() === s.name?.toLowerCase().trim());
      if (matchedDir && enriched.type === 'individual') {
        return {
          ...enriched,
          isPEP: enriched.isPEP ?? matchedDir.isPEP,
          pepDetails: enriched.pepDetails ?? matchedDir.pepDetails,
          taxResidencies: enriched.taxResidencies?.length ? enriched.taxResidencies : matchedDir.taxResidencies,
          isUSResident: enriched.isUSResident ?? matchedDir.isUSResident,
          giinNumber: enriched.giinNumber ?? matchedDir.giinNumber,
        };
      }
      return enriched;
    });
  });

  // ── Authorized Person nomination state ──
  const [authorizedPersonContact, setAuthorizedPersonContact] = useState<AuthorizedPersonContact>(() => {
    // Restore from saved data if available
    const saved = (company as any)?.authorizedPersonContact;
    return saved || {
      selectedDirectorIndex: null,
      isApplicant: false,
      isSomeoneElse: false,
      email: '',
      phoneCountryCode: '+44',
      phoneNumber: '',
      fullName: '',
    };
  });
  const [showAuthPhonePicker, setShowAuthPhonePicker] = useState(false);
  const [authPhoneSearch, setAuthPhoneSearch] = useState('');

  const [activeDirectorIndex, setActiveDirectorIndex] = useState<number | null>(null);
  const [activeShareholderIndex, setActiveShareholderIndex] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationSummary, setValidationSummary] = useState<string[]>([]);
  const hasFetched = useRef(false);

  // ── Re-fetch CreditSafe report ────────────────────────────
  useEffect(() => {
    if (hasFetched.current) return;
    const connectId = company?.connectId || undefined;
    const regNo = company?.registrationNumber;
    const countryCode = company?.countryCode;

    if (!countryCode || company?.isManualEntry) return;
    if (!connectId && !regNo) return;

    // Skip fetch if user has already confirmed declarations (data already enriched)
    const alreadyConfirmed = (company?.directors || []).some((d: any) => d.isPEP !== undefined);
    if (alreadyConfirmed) return;

    hasFetched.current = true;
    setRefreshing(true);
    setRefreshError(null);

    supabase.functions.invoke('creditsafe-company', {
      body: { action: 'report', connectId, regNo, countryCode },
    }).then(({ data: res, error }) => {
      if (error || !res?.success) {
        setRefreshError('Could not refresh from registry — using existing data');
        return;
      }

      const fresh = res.data;
      const cc = countryCode || '';

      const freshDirectors: any[] = fresh?.directors || [];
      if (freshDirectors.length > 0) {
        setDirectors(prev => {
          const enriched = freshDirectors.map((fd: any) => {
            const enrichedFd = enrichPerson(fd, cc);
            const existing = prev.find(p => p.name?.toLowerCase().trim() === fd.name?.toLowerCase().trim());
            return {
              ...enrichedFd,
              isPEP: existing?.isPEP,
              pepDetails: existing?.pepDetails,
              taxResidencies: existing?.taxResidencies,
              isUSResident: existing?.isUSResident,
              giinNumber: existing?.giinNumber,
              declarationsCompleted: existing?.declarationsCompleted,
            };
          });
          const manualOnes = prev.filter(p => p.isManuallyAdded);
          return [...enriched, ...manualOnes];
        });
      } else if (fresh?.reportUnavailable && directors.length === 0) {
        setRefreshError('Director data unavailable from registry — please add directors manually');
      }

      const freshShareholders: any[] = fresh?.shareholders || [];
      if (freshShareholders.length > 0) {
        setShareholders(prev => {
          const enriched = freshShareholders.map((fs: any) => {
            const enrichedFs = {
              ...enrichPerson(fs, cc),
              type: (fs.type || (isCompanyName(fs.name) ? 'company' : 'individual')) as 'individual' | 'company',
              ubos: fs.ubos || [],
            };
            const existing = prev.find(p => p.name?.toLowerCase().trim() === fs.name?.toLowerCase().trim());
            return {
              ...enrichedFs,
              isPEP: existing?.isPEP,
              pepDetails: existing?.pepDetails,
              taxResidencies: existing?.taxResidencies,
              isUSResident: existing?.isUSResident,
              giinNumber: existing?.giinNumber,
              declarationsCompleted: existing?.declarationsCompleted,
            };
          });
          const manualOnes = prev.filter(p => p.isManuallyAdded);
          return [...enriched, ...manualOnes];
        });
      }
    }).finally(() => setRefreshing(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Enrich from Sumsub biometrics ──
  useEffect(() => {
    const savedBiometrics: any[] = (data as any).corporateBiometrics || [];
    if (!savedBiometrics.length) return;

    setDirectors(prev =>
      prev.map(d => {
        const bio = savedBiometrics.find(b => b.name?.toLowerCase().trim() === d.name?.toLowerCase().trim());
        if (!bio?.sumsubData) return d;
        const sd = bio.sumsubData;
        return {
          ...d,
          dateOfBirth: sd.dateOfBirth || d.dateOfBirth,
          nationality: sd.document?.nationality || d.nationality,
          address: {
            street: sd.poa?.address?.street || sd.address?.street || d.address.street,
            city: sd.poa?.address?.city || sd.address?.city || d.address.city,
            postalCode: sd.poa?.address?.postalCode || sd.address?.postalCode || d.address.postalCode,
            country: sd.poa?.address?.country || sd.address?.country || d.address.country,
          },
        };
      })
    );

    setShareholders(prev =>
      prev.map(s => {
        const bio = savedBiometrics.find(b => b.name?.toLowerCase().trim() === s.name?.toLowerCase().trim());
        if (!bio?.sumsubData) return s;
        const sd = bio.sumsubData;
        return {
          ...s,
          address: {
            street: sd.poa?.address?.street || sd.address?.street || s.address.street,
            city: sd.poa?.address?.city || sd.address?.city || s.address.city,
            postalCode: sd.poa?.address?.postalCode || sd.address?.postalCode || s.address.postalCode,
            country: sd.poa?.address?.country || sd.address?.country || s.address.country,
          },
        };
      })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalShareholding = shareholders.reduce((sum, s) => sum + (s.shareholding || 0), 0);
  const isOwnershipValid = totalShareholding === 100;

  if (!company) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 pt-24">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-muted-foreground">Company data not found.</p>
        </div>
      </div>
    );
  }

  const updateDirectorField = (index: number, field: string, value: string) => {
    const updated = [...directors];
    if (field.startsWith('address.')) {
      const af = field.split('.')[1] as keyof Director['address'];
      updated[index] = { ...updated[index], address: { ...updated[index].address, [af]: value } };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setDirectors(updated);
  };

  const updateDirectorDeclarations = (index: number, updates: Partial<PersonDeclarationData>) => {
    const updated = [...directors];
    updated[index] = { ...updated[index], ...updates };
    setDirectors(updated);
    const dirName = updated[index].name?.toLowerCase().trim();
    if (dirName) {
      setShareholders(prev => prev.map(s =>
        s.type === 'individual' && s.name?.toLowerCase().trim() === dirName
          ? { ...s, ...updates }
          : s
      ));
    }
  };

  const updateShareholderField = (index: number, field: string, value: string | number) => {
    const updated = [...shareholders];
    if (field.startsWith('address.')) {
      const af = field.split('.')[1] as keyof Shareholder['address'];
      updated[index] = { ...updated[index], address: { ...updated[index].address, [af]: value as string } };
    } else if (field === 'name') {
      const newType = isCompanyName(value as string) ? 'company' : 'individual';
      updated[index] = { ...updated[index], name: value as string, type: newType, ubos: newType === 'company' ? [] : undefined };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setShareholders(updated);
  };

  const updateShareholderDeclarations = (index: number, updates: Partial<PersonDeclarationData>) => {
    const updated = [...shareholders];
    updated[index] = { ...updated[index], ...updates };
    setShareholders(updated);
    const shName = updated[index].name?.toLowerCase().trim();
    if (shName) {
      setDirectors(prev => prev.map(d =>
        d.name?.toLowerCase().trim() === shName
          ? { ...d, ...updates }
          : d
      ));
    }
  };

  const isShareholderAlsoDirector = (shareholder: Shareholder): Director | undefined =>
    directors.find(d => d.name?.toLowerCase().trim() === shareholder.name?.toLowerCase().trim());

  const addDirector = () => {
    const updated = [...directors, { ...emptyDirector }];
    setDirectors(updated);
    setActiveDirectorIndex(updated.length - 1);
  };

  const removeDirector = (index: number) => {
    if (directors.length <= 1) return;
    setDirectors(directors.filter((_, i) => i !== index));
    setActiveDirectorIndex(null);
    // Reset auth person selection if this director was selected
    if (authorizedPersonContact.selectedDirectorIndex === index) {
      setAuthorizedPersonContact(prev => ({ ...prev, selectedDirectorIndex: null, isApplicant: false }));
    }
  };

  const addShareholder = () => {
    const updated = [...shareholders, { ...emptyShareholder }];
    setShareholders(updated);
    setActiveShareholderIndex(updated.length - 1);
  };

  const removeShareholder = (index: number) => {
    if (shareholders.length <= 1) return;
    setShareholders(shareholders.filter((_, i) => i !== index));
    setActiveShareholderIndex(null);
  };

  // ── Authorized person helpers ────────────────────────────
  // The applicant's own email/phone from context
  const applicantEmail = data.email || '';
  const applicantPhone = data.phoneNumber || '';
  const applicantPhoneCode = data.countryCode || '+44';

  const getAuthorizedPersonName = (): string => {
    if (authorizedPersonContact.isApplicant) return 'You (the applicant)';
    if (authorizedPersonContact.isSomeoneElse) return authorizedPersonContact.fullName || 'Other authorised person';
    if (authorizedPersonContact.selectedDirectorIndex !== null) {
      return directors[authorizedPersonContact.selectedDirectorIndex]?.name || '';
    }
    return '';
  };

  const isAuthPersonComplete = (): boolean => {
    if (authorizedPersonContact.isApplicant) return true;
    if (authorizedPersonContact.selectedDirectorIndex !== null) {
      // Director selected — no extra info needed here; contact collected in biometrics
      return true;
    }
    if (authorizedPersonContact.isSomeoneElse) {
      // Need name, email and phone for someone new
      return !!(
        authorizedPersonContact.fullName?.trim() &&
        authorizedPersonContact.email.trim() &&
        authorizedPersonContact.phoneNumber.trim()
      );
    }
    return false;
  };

  const selectDirectorAsAuthorized = (index: number) => {
    const isApplicant = index === -1;
    setAuthorizedPersonContact({
      selectedDirectorIndex: isApplicant ? null : index,
      isApplicant,
      isSomeoneElse: false,
      email: isApplicant ? applicantEmail : '',
      phoneCountryCode: isApplicant ? applicantPhoneCode : '+44',
      phoneNumber: isApplicant ? applicantPhone : '',
      fullName: '',
    });
  };

  const selectSomeoneElse = () => {
    setAuthorizedPersonContact(prev => ({
      ...prev,
      selectedDirectorIndex: null,
      isApplicant: false,
      isSomeoneElse: true,
      email: '',
      phoneCountryCode: '+44',
      phoneNumber: '',
      fullName: '',
    }));
  };

  // ── Validation ────────────────────────────────────────────
  const directorNameSet = directors.map(d => d.name?.toLowerCase().trim()).filter(Boolean);

  const collectAllErrors = (): string[] => {
    const errors: string[] = [];
    directors.forEach((d, i) => {
      const errs = getDirectorErrors(d);
      errs.forEach(e => errors.push(`Director ${i + 1} (${d.name || 'unnamed'}): ${e}`));
    });
    shareholders.forEach((s, i) => {
      const errs = getShareholderErrors(s, directorNameSet);
      errs.forEach(e => errors.push(`Shareholder ${i + 1} (${s.name || 'unnamed'}): ${e}`));
    });
    if (!isOwnershipValid) errors.push(`Total ownership is ${totalShareholding}% — must equal 100%`);
    if (!isAuthPersonComplete()) errors.push('Please select who the authorized person is');
    return errors;
  };

  const handleContinue = () => {
    const errors = collectAllErrors();
    if (errors.length > 0) {
      setShowValidationErrors(true);
      setValidationSummary(errors);
      const firstDirError = directors.findIndex(d => getDirectorErrors(d).length > 0);
      const firstShError = shareholders.findIndex(s => getShareholderErrors(s, directorNameSet).length > 0);
      if (firstDirError >= 0) {
        setActiveDirectorIndex(firstDirError);
      } else if (firstShError >= 0) {
        setActiveShareholderIndex(firstShError);
      }
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      return;
    }

    // Build authorized person data to pass forward
    let authPersonData: any = null;
    if (authorizedPersonContact.isApplicant) {
      authPersonData = {
        isApplicant: true,
        email: applicantEmail,
        phoneCountryCode: applicantPhoneCode,
        phoneNumber: applicantPhone,
        name: 'Applicant',
      };
    } else if (authorizedPersonContact.selectedDirectorIndex !== null) {
      const dir = directors[authorizedPersonContact.selectedDirectorIndex];
      authPersonData = {
        isApplicant: false,
        isSomeoneElse: false,
        directorIndex: authorizedPersonContact.selectedDirectorIndex,
        name: dir.name,
        // Contact info collected later in biometrics step
      };
    } else if (authorizedPersonContact.isSomeoneElse) {
      authPersonData = {
        isApplicant: false,
        isSomeoneElse: true,
        name: authorizedPersonContact.fullName,
        email: authorizedPersonContact.email,
        phoneCountryCode: authorizedPersonContact.phoneCountryCode,
        phoneNumber: authorizedPersonContact.phoneNumber,
      };
    }

    updateData({
      corporateCompany: {
        ...company,
        directors,
        shareholders,
        authorizedPersonContact: authorizedPersonContact,
        // Pre-populate authorized person struct
        ...(authorizedPersonContact.selectedDirectorIndex !== null
          ? {
              authorizedPerson: {
                ...(company.authorizedPerson || {}),
                firstName: directors[authorizedPersonContact.selectedDirectorIndex]?.name?.split(' ')[0] || '',
                lastName: directors[authorizedPersonContact.selectedDirectorIndex]?.name?.split(' ').slice(1).join(' ') || '',
                email: (company.authorizedPerson?.email) || '',
                phoneNumber: (company.authorizedPerson?.phoneNumber) || '',
                jobTitle: directors[authorizedPersonContact.selectedDirectorIndex]?.occupation || 'Director',
                isDirector: true,
                isExistingPerson: true,
              }
            }
          : authorizedPersonContact.isSomeoneElse
          ? {
              authorizedPerson: {
                ...(company.authorizedPerson || {}),
                firstName: authorizedPersonContact.fullName?.split(' ')[0] || '',
                lastName: authorizedPersonContact.fullName?.split(' ').slice(1).join(' ') || '',
                email: authorizedPersonContact.email,
                phoneNumber: authorizedPersonContact.phoneNumber,
                phoneCountryCode: authorizedPersonContact.phoneCountryCode,
                jobTitle: 'Authorised Representative',
                isDirector: false,
                isExistingPerson: false,
              }
            }
          : {}),
      }
    });
    goToNextStep();
  };

  const renderEditableField = (
    label: string,
    value: string | number,
    fieldKey: string,
    onUpdate: (field: string, value: string | number) => void,
    isNumber = false,
    hasError = false
  ) => {
    const isEditing = editingField === fieldKey;
    return (
      <div className={`flex justify-between items-center py-2 border-b border-border/50 last:border-0 ${hasError ? 'bg-destructive/5 -mx-1 px-1 rounded' : ''}`}>
        <span className={`text-sm ${hasError ? 'text-destructive' : 'text-muted-foreground'}`}>{label}:{hasError && <span className="ml-1 text-destructive text-xs">*</span>}</span>
        {isEditing ? (
          <Input
            type={isNumber ? 'number' : 'text'}
            value={value}
            onChange={e => onUpdate(fieldKey, isNumber ? parseFloat(e.target.value) || 0 : e.target.value)}
            onBlur={() => setEditingField(null)}
            onKeyDown={e => e.key === 'Enter' && setEditingField(null)}
            className={`w-1/2 h-7 text-right text-sm ${hasError ? 'border-destructive' : ''}`}
            autoFocus
            min={isNumber ? 0 : undefined}
            max={isNumber ? 100 : undefined}
          />
        ) : (
          <span
            className={`font-medium text-right cursor-pointer transition-colors text-sm flex items-center gap-1 ${hasError ? 'text-destructive hover:text-destructive/80' : 'hover:text-primary'}`}
            onClick={() => setEditingField(fieldKey)}
          >
            {isNumber ? `${value}%` : value || <span className={`italic ${hasError ? 'text-destructive/70' : 'text-muted-foreground'}`}>{hasError ? 'Required — tap to fill' : 'Click to edit'}</span>}
            <Edit2 className="w-3 h-3 text-muted-foreground" />
          </span>
        )}
      </div>
    );
  };

  // Declarations (PEP/Tax/FATCA) are now collected after Sumsub in a later step.
  // Treat the cap-table row as "complete" once basic info is present.
  const declarationComplete = (_p: PersonDeclarationData) => true;

  const directorHasErrors = (d: Director) => showValidationErrors && getDirectorErrors(d).length > 0;
  const shareholderHasErrors = (s: Shareholder) => showValidationErrors && getShareholderErrors(s, directorNameSet).length > 0;

  const filteredAuthPhoneCodes = authPhoneSearch
    ? phoneCodesSorted.filter(c => c.country.toLowerCase().includes(authPhoneSearch.toLowerCase()) || c.code.includes(authPhoneSearch))
    : phoneCodesSorted;

  const selectedAuthPhoneCountry = phoneCodes.find(c => c.code === authorizedPersonContact.phoneCountryCode) || phoneCodes[0];

  return (
    <div className="min-h-screen bg-background flex flex-col pt-20 pb-28 px-4 sm:px-6">
      <div className="flex-1 flex flex-col max-w-md mx-auto w-full">
        <div className="animate-fade-in-up">
          <h1 className="text-xl sm:text-2xl font-bold text-center tracking-[0.15em] mb-1">
            COMPANY STRUCTURE
          </h1>
          <p className="text-muted-foreground text-center text-xs mb-4">
            Complete declarations for each director and shareholder
          </p>

          {/* Registry refresh banner */}
          {refreshing && (
            <div className="flex items-center gap-2 p-3 mb-3 rounded-xl bg-accent/10 border border-accent/20 text-accent text-xs">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <span>Fetching latest data from company registry…</span>
            </div>
          )}
          {!refreshing && refreshError && (
            <div className="flex items-center gap-2 p-3 mb-3 rounded-xl bg-muted/50 border border-border text-muted-foreground text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{refreshError}</span>
            </div>
          )}

          {/* Validation error summary */}
          {showValidationErrors && validationSummary.length > 0 && (
            <div className="p-3 mb-3 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive text-xs space-y-1">
              <p className="font-semibold flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />Please fix the following before continuing:</p>
              <ul className="list-disc list-inside space-y-0.5 pl-1">
                {validationSummary.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {/* ── Directors section ── */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Directors</span>
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{directors.length}</span>
              </div>
              <button
                onClick={addDirector}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>

            <div className="space-y-2">
              {directors.map((director, index) => {
                const expanded = activeDirectorIndex === index;
                const done = declarationComplete(director);
                const hasErr = directorHasErrors(director);
                const fieldErrors = showValidationErrors ? getDirectorFieldErrors(director) : {};
                const isAuthPerson = !authorizedPersonContact.isApplicant && authorizedPersonContact.selectedDirectorIndex === index;
                return (
                  <div key={index}
                    className={`rounded-xl border bg-card transition-colors ${
                      hasErr ? 'border-destructive/60' : expanded ? 'border-primary' : 'border-border hover:border-primary/30'
                    }`}>
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer"
                      onClick={() => setActiveDirectorIndex(expanded ? null : index)}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${hasErr ? 'bg-destructive/10' : done ? 'bg-success/10' : 'bg-primary/10'}`}>
                            {done && !hasErr
                              ? <Check className="w-4 h-4 text-success" />
                              : <User className={`w-4 h-4 ${hasErr ? 'text-destructive' : 'text-primary'}`} />}
                          </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{director.name || 'New Director'}</p>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-xs text-muted-foreground">{director.occupation || 'Director'}</p>
                            {isAuthPerson && (
                              <Badge variant="secondary" className="text-[10px] py-0 px-1.5 bg-accent/15 text-accent border-0">
                                Authorised Person
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        {hasErr && <AlertCircle className="w-4 h-4 text-destructive" />}
                        {director.isManuallyAdded && directors.length > 1 && (
                          <button onClick={e => { e.stopPropagation(); removeDirector(index); }}
                            className="p-1 rounded text-destructive hover:bg-destructive/10 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    </div>

                    {expanded && (
                      <div className="px-3 pb-3" onClick={e => e.stopPropagation()}>
                        <div className="pt-2 border-t border-border/50">
                          {renderEditableField('Full Name', director.name, `d-${index}-name`, (f, v) => updateDirectorField(index, 'name', v as string), false, !!fieldErrors['name'])}
                          {renderEditableField('Role', director.occupation, `d-${index}-occupation`, (f, v) => updateDirectorField(index, 'occupation', v as string))}
                          {director.appointedDate && renderEditableField('Appointed', director.appointedDate, `d-${index}-appointedDate`, (f, v) => updateDirectorField(index, 'appointedDate', v as string))}
                        </div>
                        <div className="mt-2 px-2 py-1.5 rounded-lg bg-muted/40 border border-border/50 flex items-start gap-2">
                          <Shield className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                          <p className="text-xs text-muted-foreground">
                            Identity details (DOB, nationality, address) and PEP / tax / FATCA declarations are collected after biometric ID verification, in a later step.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Shareholders section ── */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Shareholders</span>
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">{shareholders.length}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isOwnershipValid
                    ? 'bg-green-500/10 text-green-600'
                    : showValidationErrors
                      ? 'bg-destructive/10 text-destructive'
                      : 'bg-amber-500/10 text-amber-600'
                }`}>
                  {totalShareholding}% {isOwnershipValid ? '✓' : '/ 100%'}
                </span>
              </div>
              <button
                onClick={addShareholder}
                className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>

            <div className="space-y-2">
              {shareholders.map((shareholder, index) => {
                const expanded = activeShareholderIndex === index;
                const isIndividual = shareholder.type === 'individual';
                const matchedDir = isShareholderAlsoDirector(shareholder);
                const done = isIndividual
                  ? declarationComplete(shareholder) || (!!matchedDir && declarationComplete(matchedDir))
                  : true;
                const hasErr = shareholderHasErrors(shareholder);
                const fieldErrors = showValidationErrors ? getShareholderFieldErrors(shareholder, directorNameSet) : {};
                return (
                  <div key={index}
                    className={`rounded-xl border bg-card transition-colors ${
                      hasErr ? 'border-destructive/60' : expanded ? 'border-primary' : 'border-border hover:border-primary/30'
                    }`}>
                    <div
                      className="flex items-center justify-between p-3 cursor-pointer"
                      onClick={() => setActiveShareholderIndex(expanded ? null : index)}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${hasErr ? 'bg-destructive/10' : done && isIndividual ? 'bg-success/10' : 'bg-primary/10'}`}>
                            {done && isIndividual && !hasErr
                              ? <Check className="w-4 h-4 text-success" />
                              : shareholder.type === 'company'
                                ? <Building2 className={`w-4 h-4 ${hasErr ? 'text-destructive' : 'text-primary'}`} />
                                : <User className={`w-4 h-4 ${hasErr ? 'text-destructive' : 'text-primary'}`} />}
                          </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{shareholder.name || 'New Shareholder'}</p>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs font-semibold ${hasErr ? 'text-destructive' : 'text-muted-foreground'}`}>{shareholder.shareholding}%</span>
                            <span className="text-xs text-muted-foreground/60">·</span>
                            <span className="text-xs text-muted-foreground">{shareholder.type === 'company' ? 'Corporate' : 'Individual'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        {hasErr && <AlertCircle className="w-4 h-4 text-destructive" />}
                        {shareholder.isManuallyAdded && shareholders.length > 1 && (
                          <button onClick={e => { e.stopPropagation(); removeShareholder(index); }}
                            className="p-1 rounded text-destructive hover:bg-destructive/10 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                      </div>
                    </div>

                    {expanded && (
                      <div className="px-3 pb-3" onClick={e => e.stopPropagation()}>
                        <div className="pt-2 border-t border-border/50">
                          {renderEditableField('Name', shareholder.name, `s-${index}-name`, (f, v) => updateShareholderField(index, 'name', v), false, !!fieldErrors['name'])}
                          {renderEditableField('Ownership %', shareholder.shareholding, `s-${index}-shareholding`, (f, v) => updateShareholderField(index, 'shareholding', v), true, !!fieldErrors['shareholding'])}
                          {renderEditableField('Share Class', shareholder.shareClass, `s-${index}-shareClass`, (f, v) => updateShareholderField(index, 'shareClass', v as string))}
                        </div>

                        {shareholder.type === 'company' && shareholder.ubos && shareholder.ubos.length > 0 && (
                          <div className="mt-2 p-2 rounded-lg bg-muted/50">
                            <p className="text-xs font-medium mb-1">Beneficial Owners ({shareholder.ubos.length})</p>
                            {shareholder.ubos.map((ubo, uboIdx) => (
                              <div key={uboIdx} className="flex justify-between text-xs py-1">
                                <span>{ubo.name}</span>
                                <span className="text-muted-foreground">{ubo.type === 'individual' ? `${ubo.shareholding}% — Individual` : `${ubo.shareholding}% — Company`}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {isIndividual && (
                          <div className="mt-2 px-2 py-1.5 rounded-lg bg-muted/40 border border-border/50 flex items-start gap-2">
                            <Shield className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
                            <p className="text-xs text-muted-foreground">
                              Identity details and PEP / tax / FATCA declarations are collected after biometric ID verification, in a later step.
                            </p>
                          </div>
                        )}

                        {shareholder.type === 'company' && (
                          <div className="mt-2 p-2 rounded-lg bg-primary/5 border border-primary/20">
                            <p className="text-xs text-primary">
                              UBO individuals traced from this corporate shareholder will complete declarations in the next step.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Authorized Person Section ── */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Authorised Person</span>
              {isAuthPersonComplete() && (
                <Check className="w-3.5 h-3.5 text-success" />
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Who is authorised to act on behalf of {company.name} and is completing this application?
            </p>

            <div className={`rounded-xl border bg-card overflow-hidden ${showValidationErrors && !isAuthPersonComplete() ? 'border-destructive/60' : 'border-border'}`}>

              {/* Option: Each director — no extra fields needed, they are known */}
              {directors.filter(d => d.name?.trim()).map((director, index) => {
                const isSelected = !authorizedPersonContact.isApplicant && !authorizedPersonContact.isSomeoneElse && authorizedPersonContact.selectedDirectorIndex === index;
                return (
                  <button
                    key={index}
                    onClick={() => selectDirectorAsAuthorized(index)}
                    className={`w-full flex items-center gap-3 p-3 border-b border-border/50 transition-colors ${
                      isSelected ? 'bg-accent/10' : 'hover:bg-muted/40'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected ? 'border-accent bg-accent' : 'border-border'
                    }`}>
                      {isSelected && <Check className="w-3.5 h-3.5 text-accent-foreground" />}
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{director.name}</p>
                      <p className="text-xs text-muted-foreground">{director.occupation || 'Director'}</p>
                    </div>
                  </button>
                );
              })}

              {/* Option: Someone else — a new person not in the director list */}
              <div>
                <button
                  onClick={selectSomeoneElse}
                  className={`w-full flex items-center gap-3 p-3 transition-colors ${
                    authorizedPersonContact.isSomeoneElse ? 'bg-accent/10' : 'hover:bg-muted/40'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    authorizedPersonContact.isSomeoneElse ? 'border-accent bg-accent' : 'border-border'
                  }`}>
                    {authorizedPersonContact.isSomeoneElse && <Check className="w-3.5 h-3.5 text-accent-foreground" />}
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="text-sm font-medium">Someone else</p>
                    <p className="text-xs text-muted-foreground">A different authorised representative</p>
                  </div>
                </button>

                {/* Expanded form for "someone else" */}
                {authorizedPersonContact.isSomeoneElse && (
                  <div className="px-4 pb-5 pt-4 border-t border-accent/20 space-y-4 bg-muted/20">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Enter their details. They will need to verify their identity and will be contacted to complete biometric verification.
                    </p>

                    {/* Full Name */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">Full name <span className="text-destructive">*</span></label>
                      <Input
                        type="text"
                        value={authorizedPersonContact.fullName || ''}
                        onChange={e => setAuthorizedPersonContact(prev => ({ ...prev, fullName: e.target.value }))}
                        placeholder="e.g. Jane Smith"
                        className="h-10 text-sm"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">Email address <span className="text-destructive">*</span></label>
                      <Input
                        type="email"
                        value={authorizedPersonContact.email}
                        onChange={e => setAuthorizedPersonContact(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="authorised@example.com"
                        className="h-10 text-sm"
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">Mobile number <span className="text-destructive">*</span></label>

                      {/* Trigger row */}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => { setShowAuthPhonePicker(v => !v); setAuthPhoneSearch(''); }}
                          className="flex items-center gap-1.5 px-3 h-10 rounded-lg border border-input bg-background hover:bg-muted/40 transition-colors text-sm whitespace-nowrap shrink-0"
                        >
                          <FlagImg code={selectedAuthPhoneCountry.iso2} size="xs" />
                          <span className="font-medium">{selectedAuthPhoneCountry.code}</span>
                          <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform duration-200 ${showAuthPhonePicker ? 'rotate-180' : ''}`} />
                        </button>
                        <Input
                          type="tel"
                          value={authorizedPersonContact.phoneNumber}
                          onChange={e => setAuthorizedPersonContact(prev => ({ ...prev, phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 15) }))}
                          placeholder="Mobile number"
                          className="h-10 text-sm flex-1"
                        />
                      </div>

                      {/* Inline search list — rendered in normal flow, below the row */}
                      {showAuthPhonePicker && (
                        <div className="rounded-lg border border-border bg-popover overflow-hidden w-56">
                          <input
                            type="text"
                            placeholder="Search country..."
                            value={authPhoneSearch}
                            autoFocus
                            onChange={e => setAuthPhoneSearch(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-muted/40 border-b border-border focus:outline-none placeholder:text-muted-foreground"
                            style={{ fontSize: '16px' }}
                          />
                          <div className="max-h-44 overflow-y-auto">
                            {authPhoneSearch.trim() === '' ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground text-center">Start typing to search…</p>
                            ) : filteredAuthPhoneCodes.length === 0 ? (
                              <p className="px-3 py-3 text-xs text-muted-foreground text-center">No results</p>
                            ) : filteredAuthPhoneCodes.slice(0, 30).map((c, i) => (
                              <button
                                key={`${c.code}-${i}`}
                                type="button"
                                onClick={() => {
                                  setAuthorizedPersonContact(prev => ({ ...prev, phoneCountryCode: c.code }));
                                  setShowAuthPhonePicker(false);
                                  setAuthPhoneSearch('');
                                }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted/60 transition-colors text-left text-xs border-b border-border/30 last:border-0"
                              >
                                <FlagImg code={c.iso2} size="xs" />
                                <span className="flex-1 truncate text-foreground">{c.country}</span>
                                <span className="text-muted-foreground font-medium shrink-0">{c.code}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Their address, date of birth and other details will be extracted automatically during identity verification.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {showValidationErrors && !isAuthPersonComplete() && (
              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Please select or enter the authorised person's details
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border safe-area-inset-bottom">
        <div className="max-w-md mx-auto">
          <Button
            onClick={handleContinue}
            disabled={collectAllErrors().length > 0}
            className="w-full h-14 text-base font-semibold rounded-xl"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
